import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  ClickAwayListener
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportArticle, exportArticleBundle } from '../../services/api';
import { useSnackbar } from 'notistack';

const ArticleTopBar = ({
  article,
  user,
  onEditClick,
  onImageGeneratorOpen,
  onTitleChange
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [exportBundleLoading, setExportBundleLoading] = useState(false);
  const [exportFormatLoading, setExportFormatLoading] = useState(false);
  const [currentExportFormat, setCurrentExportFormat] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(article.title);
  const [titleLoading, setTitleLoading] = useState(false);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportFormat = async (format) => {
    handleExportClose();
    try {
      setExportFormatLoading(true);
      setCurrentExportFormat(format);
      await exportArticle(article.id, format);
      enqueueSnackbar(`Successfully exported as ${format.toUpperCase()}`, { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar(error.message || 'Failed to export article', { 
        variant: 'error' 
      });
    } finally {
      setExportFormatLoading(false);
      setCurrentExportFormat(null);
    }
  };

  const handleExportBundle = async () => {
    try {
      setExportBundleLoading(true);
      await exportArticleBundle(article.id);
      enqueueSnackbar('Successfully exported bundle', { variant: 'success' });
    } catch (error) {
      console.error('Export bundle error:', error);
      enqueueSnackbar(error.message || 'Failed to export article bundle', { 
        variant: 'error' 
      });
    } finally {
      setExportBundleLoading(false);
    }
  };

  const getFormatLabel = (format) => {
    const labels = {
      pdf: 'PDF Document',
      docx: 'Word Document',
      html: 'HTML',
      md: 'Markdown',
      txt: 'Plain Text',
      rtf: 'Rich Text',
      zip: 'All Formats'
    };
    return labels[format] || format.toUpperCase();
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setTitleValue(article.title);
  };

  const handleTitleSubmit = async () => {
    const newTitle = titleValue.trim();
    if (newTitle !== article.title && newTitle !== '') {
      try {
        setTitleLoading(true);
        await onTitleChange?.(newTitle);
        enqueueSnackbar('Title updated successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error updating title:', error);
        enqueueSnackbar(error.message || 'Failed to update title', { variant: 'error' });
        // Revert to original title on error
        setTitleValue(article.title);
      } finally {
        setTitleLoading(false);
      }
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleTitleSubmit();
    } else if (event.key === 'Escape') {
      setIsEditingTitle(false);
      setTitleValue(article.title);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: { xs: 1, sm: 2.5 },
      p: { xs: 1, sm: 2 }
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 2 },
        flexWrap: 'nowrap',
        width: '100%'
      }}>
        <ClickAwayListener onClickAway={() => {
          if (isEditingTitle) {
            handleTitleSubmit();
          }
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isEditingTitle ? (
              <TextField
                fullWidth
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                autoFocus
                disabled={titleLoading}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: titleLoading && (
                    <CircularProgress size={20} />
                  ),
                  style: {
                    fontSize: isMobile ? '1.1rem' : '1.75rem',
                    padding: isMobile ? '8px 12px' : '12px 16px',
                    lineHeight: 1.2
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
            ) : (
              <Typography 
                variant="h4" 
                component="h1"
                onClick={handleTitleClick}
                sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.75rem', md: '2.125rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {article.title}
              </Typography>
            )}
          </Box>
        </ClickAwayListener>
        <Box sx={{ 
          display: 'flex',
          gap: { xs: 0.5, sm: 1 },
          alignItems: 'center',
          ml: 'auto'
        }}>
          {user?.firebaseUser?.email === 'dmytro.de.ch@gmail.com' && (
            <Tooltip title="Export Bundle">
              <IconButton
                onClick={handleExportBundle}
                disabled={exportBundleLoading || exportFormatLoading}
                color="primary"
                size={isMobile ? "medium" : "small"}
                sx={{ 
                  width: { xs: 40, sm: 'auto' },
                  height: { xs: 40, sm: 'auto' }
                }}
              >
                {exportBundleLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FolderZipIcon />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Export As">
            <IconButton
              onClick={handleExportClick}
              disabled={exportBundleLoading || exportFormatLoading}
              color="primary"
              size={isMobile ? "medium" : "small"}
              sx={{ 
                width: { xs: 40, sm: 'auto' },
                height: { xs: 40, sm: 'auto' }
              }}
            >
              {exportFormatLoading ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              )}
            </IconButton>
          </Tooltip>
          <IconButton 
            onClick={onEditClick} 
            color="primary" 
            size={isMobile ? "medium" : "small"}
            sx={{ 
              width: { xs: 40, sm: 'auto' },
              height: { xs: 40, sm: 'auto' }
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            onClick={() => onImageGeneratorOpen()} 
            color="primary" 
            size={isMobile ? "medium" : "small"}
            sx={{ 
              width: { xs: 40, sm: 'auto' },
              height: { xs: 40, sm: 'auto' }
            }}
          >
            <ImageIcon />
          </IconButton>
        </Box>
      </Box>
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 320 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: 'none' }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => handleExportFormat('pdf')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'pdf' ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="PDF Document" 
            secondary="With embedded images"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('docx')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'docx' ? <CircularProgress size={20} /> : <DescriptionIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="Word Document" 
            secondary="With embedded images"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('html')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'html' ? <CircularProgress size={20} /> : <CodeIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="HTML" 
            secondary="With separate image files"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('md')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'md' ? <CircularProgress size={20} /> : <TextSnippetIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="Markdown" 
            secondary="With image links"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('txt')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'txt' ? <CircularProgress size={20} /> : <TextSnippetIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="Plain Text" 
            secondary="Text only"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('rtf')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'rtf' ? <CircularProgress size={20} /> : <DescriptionIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="Rich Text" 
            secondary="Basic formatting"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportFormat('zip')}
          disabled={exportFormatLoading || exportBundleLoading}
        >
          <ListItemIcon>
            {currentExportFormat === 'zip' ? <CircularProgress size={20} /> : <FolderZipIcon />}
          </ListItemIcon>
          <ListItemText 
            primary="All Formats" 
            secondary="Complete export package"
            primaryTypographyProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ArticleTopBar; 